import { useState, useEffect, useCallback } from "react";
import { api } from "../api/client";
import { I } from "./Icons";
import { clsx, fmtDate } from "./Primitives";

interface SharedDrive {
  id: string;
  name: string;
}

interface DriveItem {
  id: string;
  name: string;
  mimeType: string;
  isFolder: boolean;
  size: string | null;
  modifiedTime: string | null;
  webViewLink: string | null;
  iconLink: string | null;
}

interface Crumb {
  id: string;
  name: string;
  driveId?: string;
}

const MIME_META: Record<string, { label: string; color: string }> = {
  "application/vnd.google-apps.folder": { label: "Folder", color: "#5f6368" },
  "application/vnd.google-apps.document": { label: "Doc", color: "#4285F4" },
  "application/vnd.google-apps.spreadsheet": { label: "Sheet", color: "#0F9D58" },
  "application/vnd.google-apps.presentation": { label: "Slide", color: "#F4B400" },
  "application/pdf": { label: "PDF", color: "#D93025" },
  "application/vnd.google-apps.form": { label: "Form", color: "#7627BB" },
  "application/vnd.google-apps.drawing": { label: "Drawing", color: "#4285F4" },
};

function mimeLabel(mime: string): { label: string; color: string } {
  if (MIME_META[mime]) return MIME_META[mime];
  if (mime.startsWith("image/")) return { label: "Image", color: "#4285F4" };
  if (mime.startsWith("video/")) return { label: "Video", color: "#D93025" };
  if (mime.includes("zip") || mime.includes("tar") || mime.includes("rar")) return { label: "Archive", color: "#5f6368" };
  if (mime.includes("word") || mime.includes("document")) return { label: "Doc", color: "#4285F4" };
  if (mime.includes("sheet") || mime.includes("excel")) return { label: "Sheet", color: "#0F9D58" };
  if (mime.includes("presentation") || mime.includes("powerpoint")) return { label: "Slide", color: "#F4B400" };
  return { label: "File", color: "#5f6368" };
}

interface Props {
  go: (screen: string, id?: string) => void;
}

export default function Drive({ go }: Props) {
  const [drives, setDrives] = useState<SharedDrive[] | null>(null);
  const [items, setItems] = useState<DriveItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [crumbs, setCrumbs] = useState<Crumb[]>([]);
  const [preview, setPreview] = useState<DriveItem | null>(null);

  const atRoot = crumbs.length === 0;

  const loadSharedDrives = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get<SharedDrive[]>("/api/drive/shared-drives");
      setDrives(data);
      setItems([]);
      setCrumbs([]);
    } catch {
      setError("Could not load shared drives — you may need to log out and back in to grant Drive access.");
    }
    setLoading(false);
  }, []);

  const loadFolder = useCallback(async (folderId: string, driveId?: string) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (folderId) params.set("folder_id", folderId);
      if (driveId) params.set("drive_id", driveId);
      const data = await api.get<DriveItem[]>(`/api/drive/list?${params.toString()}`);
      setItems(data);
      setDrives(null);
    } catch {
      setError("Could not load folder contents.");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadSharedDrives();
  }, [loadSharedDrives]);

  const openDrive = (d: SharedDrive) => {
    setCrumbs([{ id: d.id, name: d.name, driveId: d.id }]);
    loadFolder(d.id, d.id);
  };

  const openFolder = (item: DriveItem) => {
    const parentDriveId = crumbs[0]?.driveId;
    setCrumbs(prev => [...prev, { id: item.id, name: item.name }]);
    loadFolder(item.id, parentDriveId);
  };

  const goToCrumb = (index: number) => {
    if (index < 0) {
      loadSharedDrives();
      return;
    }
    const target = crumbs[index];
    const parentDriveId = crumbs[0]?.driveId;
    setCrumbs(prev => prev.slice(0, index + 1));
    loadFolder(target.id, parentDriveId);
  };

  const folders = items.filter(i => i.isFolder);
  const files = items.filter(i => !i.isFolder);

  return (
    <div className="screen drive-screen">
      <div className="page-head">
        <div>
          <div className="page-title">Files</div>
          <div className="page-sub">
            <span className="gdrive-dot" />
            <span>Google Shared Drives · saga-advisory.com</span>
          </div>
        </div>
        <div className="drive-head-actions">
          {!atRoot && (
            <button className="btn ghost sm" onClick={() => goToCrumb(-1)}>
              {I.home({ size: 14, w: 1.8 })} Shared Drives
            </button>
          )}
          <a href="https://drive.google.com/drive/shared-drives" target="_blank" rel="noopener noreferrer"
            className="btn ghost sm" style={{ textDecoration: "none" }}>
            {I.ext({ size: 13 })} Open in Drive
          </a>
        </div>
      </div>

      {crumbs.length > 0 && (
        <div className="crumbs" style={{ marginBottom: 16 }}>
          <a onClick={() => goToCrumb(-1)}>Shared Drives</a>
          {crumbs.map((c, i) => (
            <span key={c.id}>
              <span className="dotsep">/</span>
              {i < crumbs.length - 1 ? (
                <a onClick={() => goToCrumb(i)}>{c.name}</a>
              ) : (
                <span>{c.name}</span>
              )}
            </span>
          ))}
        </div>
      )}

      {error && (
        <div className="ai-error" style={{ marginBottom: 16 }}>{error}</div>
      )}

      {loading ? (
        <div className="empty-mini">Loading…</div>
      ) : atRoot && drives ? (
        <div className="card">
          <div className="doclist">
            {drives.length === 0 ? (
              <div className="empty-mini">
                No shared drives found. Either you haven't granted Drive access yet, or your Google Workspace doesn't have shared drives set up.
              </div>
            ) : (
              drives.map(d => (
                <div key={d.id} className="doc-row" onClick={() => openDrive(d)} style={{ cursor: "pointer" }}>
                  <span className="doc-ic" style={{ color: "#F4B400" }}>
                    {I.doc({ size: 16 })}
                  </span>
                  <span className="doc-name" style={{ fontWeight: 550 }}>{d.name}</span>
                  <span className="doc-kind">Shared Drive</span>
                  <span className="doc-meta" />
                </div>
              ))
            )}
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="doclist">
            {folders.map(f => (
              <div key={f.id} className="doc-row" onClick={() => openFolder(f)} style={{ cursor: "pointer" }}>
                <span className="doc-ic" style={{ color: "#5f6368" }}>
                  {I.doc({ size: 16 })}
                </span>
                <span className="doc-name" style={{ fontWeight: 550 }}>{f.name}</span>
                <span className="doc-kind">Folder</span>
                <span className="doc-meta" />
              </div>
            ))}
            {files.map(f => {
              const meta = mimeLabel(f.mimeType);
              return (
                <div key={f.id} className="doc-row" onClick={() => setPreview(f)} style={{ cursor: "pointer" }}>
                  <span className="doc-ic" style={{ color: meta.color }}>
                    {I.doc({ size: 16 })}
                  </span>
                  <span className="doc-name">{f.name}</span>
                  <span className="doc-kind">{meta.label}</span>
                  <span className="doc-meta">{f.size || ""}</span>
                </div>
              );
            })}
            {folders.length === 0 && files.length === 0 && (
              <div className="empty-mini">This folder is emptier than a Norwegian beach in January.</div>
            )}
          </div>
        </div>
      )}

      {preview && (
        <div className="cmd-overlay" onClick={() => setPreview(null)}>
          <div className="cmd-panel" onClick={e => e.stopPropagation()} style={{ maxWidth: 500 }}>
            <div style={{ padding: 20 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                <h3 style={{ margin: 0, fontFamily: "var(--font-serif)", fontSize: 18, color: "var(--ink)" }}>{preview.name}</h3>
                <button className="icon-btn" onClick={() => setPreview(null)}>
                  {I.plus({ size: 16, style: { transform: "rotate(45deg)" } })}
                </button>
              </div>
              <div style={{ fontSize: 13, color: "var(--muted)", marginBottom: 8 }}>
                {mimeLabel(preview.mimeType).label}
                {preview.size && ` · ${preview.size}`}
                {preview.modifiedTime && ` · Modified ${fmtDate(preview.modifiedTime)}`}
              </div>
              {preview.webViewLink && (
                <a href={preview.webViewLink} target="_blank" rel="noopener noreferrer" className="btn ghost sm" style={{ textDecoration: "none", marginTop: 12 }}>
                  {I.ext({ size: 14 })} Open in Google Drive
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
